import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Buttons of the new design (design guide: 44/48 px, primary with the button
 * shadow, text buttons, every state explicit). Tokens only.
 */

export const focusRing =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-g-ring";

interface PrimaryButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean;
  children: ReactNode;
}

export function PrimaryButton({
  loading = false,
  disabled,
  className,
  children,
  ...props
}: PrimaryButtonProps) {
  return (
    <button
      type="button"
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        "inline-flex h-12 items-center justify-center gap-2 rounded-g-md bg-g-primary px-5 text-[15px] font-semibold text-g-on-primary shadow-g-button transition-colors duration-150 ease-out",
        "hover:bg-g-primary-hover active:bg-g-primary-press",
        "disabled:cursor-not-allowed disabled:bg-g-primary-muted disabled:shadow-none",
        focusRing,
        className,
      )}
      {...props}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
      {children}
    </button>
  );
}

interface TextButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
}

export function TextButton({ className, children, ...props }: TextButtonProps) {
  return (
    <button
      type="button"
      className={cn(
        "inline-flex h-11 items-center justify-center gap-1.5 rounded-g-sm px-3 text-[14px] font-semibold text-g-ink-600 transition-colors duration-150 ease-out",
        "hover:bg-g-primary-wash hover:text-g-ink-900 active:bg-g-primary-tint",
        "disabled:cursor-not-allowed disabled:text-g-ink-300 disabled:hover:bg-transparent",
        focusRing,
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

/** A link-looking button for secondary actions inside copy. */
export function LinkButton({ className, children, ...props }: TextButtonProps) {
  return (
    <button
      type="button"
      className={cn(
        "rounded-g-xs font-semibold text-g-primary underline-offset-4 transition-colors duration-150 hover:text-g-primary-hover hover:underline disabled:cursor-not-allowed disabled:text-g-ink-300",
        focusRing,
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}
